import type { ReactNode } from 'react';
import { Alert, Spin } from 'antd';
import { useLocation } from 'react-router-dom';

import { menuTreeCoversPath } from '@/api/rbac';
import { isPlatformOnlyPath } from '@/stores/dataScope';
import { usePermissionsStore } from '@/stores/permissions';
import NoPermissionPage from '@/pages/NoPermission';

interface Props {
  /** 单个权限码 */
  permission?: string;
  /** 任一即可（更常见——一个页面只要至少有 :read 就能进） */
  anyOf?: string[];
  /** 全部都需要 */
  allOf?: string[];
  /** 仅 SUPER_ADMIN 可访问（用于 RBAC 自身管理面板等高危页面） */
  superAdminOnly?: boolean;
  /** 权限服务降级时不放行，也不挂载业务页面。 */
  denyWhenDegraded?: boolean;
  children: ReactNode;
}

/**
 * 路由守卫：
 *   <Route element={<RequirePerm anyOf={['admin:user:read']}><UsersPage/></RequirePerm>}/>
 *
 * 权限尚未加载完成时显示 spinner，避免初次进站闪一下 403 再加载真实权限。
 */
export default function RequirePerm({
  permission,
  anyOf,
  allOf,
  superAdminOnly,
  denyWhenDegraded = false,
  children,
}: Props) {
  const loaded = usePermissionsStore((s) => s.loaded);
  const superAdmin = usePermissionsStore((s) => s.superAdmin);
  const degraded = usePermissionsStore((s) => s.degraded);
  const permissions = usePermissionsStore((s) => s.permissions);
  const menus = usePermissionsStore((s) => s.menus);
  const companyScope = usePermissionsStore((s) => s.dataScope === 'COMPANY');
  const location = useLocation();

  if (!loaded) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 64 }}>
        <Spin />
      </div>
    );
  }

  if (denyWhenDegraded && degraded) {
    return (
      <Alert
        type="warning"
        showIcon
        message="权限暂未确认，请重试"
        description="权限服务当前不可用，已停止加载赠送页面，避免沿用降级前的超级管理员判断。"
      />
    );
  }

  // 公司账号直接拼地址进平台专用页：后端会 403(90301)，这里先拦住，别让人看一屏报错。
  if (companyScope && isPlatformOnlyPath(location.pathname)) {
    return <NoPermissionPage required={['平台账号']} />;
  }

  if (superAdminOnly) {
    return superAdmin ? <>{children}</> : <NoPermissionPage required={['SUPER_ADMIN']} />;
  }

  if (superAdmin) return <>{children}</>;

  let ok = true;
  const required: string[] = [];
  if (permission) {
    required.push(permission);
    ok = permissions.has(permission);
  } else if (anyOf?.length) {
    required.push(...anyOf);
    ok = anyOf.some((c) => permissions.has(c));
  } else if (allOf?.length) {
    required.push(...allOf);
    ok = allOf.every((c) => permissions.has(c));
  }

  // 兜底：权限码没命中，但后端把这个页面拼进了 /me 菜单树 → 说明用户在这个页面下
  // 至少有一项权限（典型：只授了写权限、没授那个独立的 :read 按钮）。
  // 放他进去，接口级鉴权在后端，该 403 就 403。
  if (!ok && menuTreeCoversPath(menus, location.pathname)) ok = true;

  if (!ok) return <NoPermissionPage required={required} />;
  return <>{children}</>;
}
